/**
 * Mensalidades (spec 007, secao 12; spec 041): as contagens, a lista pra acompanhar (vencidas, a vencer em 7 dias, pagas)
 * com o filtro por etapa, e as correcoes a mao do administrador em cada parcela (marcar paga, mudar o vencimento,
 * cancelar, desfazer o "paga"), sempre com motivo. O valor e de cada matricula: vem do checkout e o administrador corrige
 * na matricula (M6). Situacao, contagens, textos e o que cabe em cada parcela (`acoes`) vem do backend: a tela so mostra.
 *
 * Na Global (modulo `assinatura`, spec 064) a mesma tela e a "Taxa de manutencao": o nome vem da API (`rotulo`), sem o
 * filtro de etapa, com as abas Suspensas e A cancelar e a coluna do valor em dolar. A Prop fica como era.
 */
import {
  Abas, Aviso, Botao, CabecalhoDePagina, Campo, CampoData, CampoDinheiro, CampoSelecao, CampoTextoLongo, Carregando, Grade, Indicador, LinkInterno, Pilha, Selo, Tabela, Vazio, formatarData, formatarDataHora, formatarDia, formatarMoeda, formatarReais, hojeEmSaoPaulo, type Tom,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { ETAPA, erroDe, tituloDaParcela, useAcao, useApi, usePapel, type AcaoDaParcela, type ContagensDeMensalidade, type LinhaDeMensalidade, type Mensalidade, type ModuloMensalidade, type SituacaoMensalidade } from "../api";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { SubcontaDoPlano } from "../subconta";
import { useAmbiente } from "../contexto";

export const TOM_DA_MENSALIDADE: Record<SituacaoMensalidade | "PAGA" | "CANCELADA", Tom> = { EM_DIA: "info", VENCE_EM_BREVE: "alerta", VENCE_HOJE: "alerta", VENCIDA: "erro", VALOR_A_DEFINIR: "neutro", SEM_COBRANCA: "neutro", SUSPENSA: "erro", PAGA: "sucesso", CANCELADA: "neutro" };
type Aba = "vencidas" | "a-vencer" | "pagas" | "suspensas" | "a-cancelar";
/** As abas que so a taxa de manutencao tem (spec 064): o prazo pra regularizar correndo e o cancelamento a confirmar. */
const ABAS_DA_TAXA: Aba[] = ["suspensas", "a-cancelar"];
/** As etapas que cobram mensalidade na Prop (o filtro do backend aceita estas). */
type EtapaQueCobra = "SIMULADOR" | "MESA_REAL";
const ETAPAS_DO_FILTRO: { valor: "" | EtapaQueCobra; rotulo: string }[] = [
  { valor: "", rotulo: "Todas as etapas" },
  { valor: "SIMULADOR", rotulo: ETAPA.SIMULADOR },
  { valor: "MESA_REAL", rotulo: ETAPA.MESA_REAL },
];
const MOTIVO_MINIMO = 5;

/** O selo de uma parcela: "Paga por fora" diz como foi; o resto e o rotulo que o backend mandou. */
export const seloDaParcela = (p: Mensalidade) => ({ tom: TOM_DA_MENSALIDADE[p.situacao], texto: p.status === "PAGA" ? (p.como_pagou === "POR_FORA" ? "Paga por fora" : "Paga") : p.situacao_rotulo });

export function useContagens(etapa: "" | EtapaQueCobra = "") {
  const { ler } = useApi();
  return useQuery({ queryKey: ["mensalidades", "contagens", etapa], queryFn: () => ler<ContagensDeMensalidade>(`/v1/admin/mensalidades/contagens${etapa ? `?etapa=${etapa}` : ""}`) });
}

/** Um dolar em reais ("5.1527" vira "R$ 5,1527"), so pra ler: a conta e do backend. */
export const cotacaoEmReais = (cotacao: string) => `R$ ${cotacao.replace(".", ",")}`;

export function PaginaMensalidades() {
  const [params, setParams] = useSearchParams();
  const { modulos } = useAmbiente();
  // A taxa de manutencao (spec 064) e do modulo `assinatura`; o filtro por etapa, so de quem tem etapas (a Prop).
  const comTaxa = modulos.includes("assinatura");
  const comEtapa = modulos.includes("etapas");
  const abasValidas: Aba[] = ["vencidas", "a-vencer", "pagas", ...(comTaxa ? ABAS_DA_TAXA : [])];
  const aba = (abasValidas.includes(params.get("aba") as Aba) ? params.get("aba") : "vencidas") as Aba;
  const etapa = (comEtapa && ["SIMULADOR", "MESA_REAL"].includes(params.get("etapa") ?? "") ? params.get("etapa") : "") as "" | EtapaQueCobra;
  // Le do endereco da janela, que muda na hora: o do roteador chega numa transicao, e trocar a aba e logo a etapa
  // apagava a aba (visto no e2e, 01/10).
  const filtrar = (mudou: { aba?: Aba; etapa?: "" | EtapaQueCobra }) => {
    const proximo = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(mudou)) {
      if (!v || (k === "aba" && v === "vencidas")) proximo.delete(k);
      else proximo.set(k, v);
    }
    setParams(proximo, { replace: true });
  };
  const { ler, postar } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const doEtapa = etapa ? `&etapa=${etapa}` : "";
  const contagens = useContagens(etapa);
  const [busca, setBusca] = useState("");
  const procurando = busca.trim().length >= 3 ? busca.trim() : "";
  const lista = useQuery({ queryKey: ["mensalidades", aba, procurando, etapa], queryFn: () => ler<{ rotulo?: string; itens: LinhaDeMensalidade[] }>(`/v1/admin/mensalidades?situacao=${aba}${procurando ? `&busca=${encodeURIComponent(procurando)}` : ""}${doEtapa}`) });
  // O nome da cobranca vem da API (a Global chama de "Taxa de manutencao"); sem ele, e a mensalidade da Prop.
  const rotulo = lista.data?.rotulo ?? (comTaxa ? "Taxa de manutenção" : "Mensalidades");
  const rotina = useAcao();
  const [feito, setFeito] = useState<string>();
  const concluir = (t: string) => { setFeito(t); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const rodar = async () => {
    const ok = await rotina.rodar(async () => {
      if (comTaxa) {
        const r = await postar<{ nasceram: number; cotadas: number; lembretes: number; suspensas: number; a_cancelar: number }>("/v1/admin/rotinas/taxas-de-manutencao");
        setFeito(`Rotina rodada: ${r.nasceram} parcela(s) nasceram, ${r.cotadas} cotada(s), ${r.lembretes} lembrete(s) pedidos, ${r.suspensas} plano(s) suspenso(s), ${r.a_cancelar} cancelamento(s) a confirmar.`);
        return;
      }
      const r = await postar<{ nasceram: number; lembretes: number; vencidas: number }>("/v1/admin/rotinas/mensalidades");
      setFeito(`Rotina rodada: ${r.nasceram} mensalidade(s) nasceram, ${r.lembretes} lembrete(s) pedidos, ${r.vencidas} vencida(s).`);
    });
    if (ok) await qc.invalidateQueries({ queryKey: ["mensalidades"] });
  };
  const descricao = comTaxa
    // O valor e os prazos sao do administrador (spec 065) e o ciclo aberto guarda o seu: a frase nao leva numero.
    ? <>Cobrada a cada ciclo enquanto a conta Funded estiver ativa, em reais pela cotação do dólar do dia em que a parcela nasce. O valor, o ciclo e os prazos ficam em <LinkInterno className="mtc-link" href="/configuracao-da-funded">Configuração da Funded</LinkInterno> e o valor de cada plano em <LinkInterno className="mtc-link" href="/planos">Planos</LinkInterno>. O cliente paga pela área: PIX ou cartão, ou PIX de recebimento com o comprovante, que você confere em Comprovantes. Vencida, o plano é suspenso no dia seguinte e você bloqueia na extranet; passado o prazo pra pagar, o cancelamento espera você na aba A cancelar.</>
    : "Cada plano paga a cada 30 dias, no valor combinado no checkout da compra. O cliente escolhe na área: por PIX ou cartão, a baixa é na hora; pelo PIX de recebimento, ele envia o comprovante e você confere em Comprovantes, o que dá a baixa. O Marcar paga fica pra quem mandou o comprovante por outro canal. Vencida bloqueia a área do cliente até a baixa, mesmo com o comprovante em análise.";
  const c = contagens.data;
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo={rotulo} descricao={descricao} acoes={pode("ADMIN") ? <Botao variante="secundario" tamanho="pequeno" carregando={rotina.carregando} onClick={() => void rodar()}>Rodar a rotina agora</Botao> : undefined} />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {rotina.erro && <Aviso tipo="erro">{rotina.erro}</Aviso>}
      <NumerosDasMensalidades c={c} carregando={contagens.isLoading} erro={contagens.isError ? erroDe(contagens.error) : undefined} />
      <div className="mtc-linha mtc-linha--entre" style={{ gap: "var(--e-4)", alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 420px", minWidth: 0 }}>
          <Abas
            rotulo={rotulo}
            valor={aba}
            aoMudar={(v) => filtrar({ aba: v })}
            abas={[
              { valor: "vencidas", rotulo: "Vencidas", contagem: c?.vencidas },
              ...(comTaxa ? [{ valor: "suspensas" as const, rotulo: "Suspensas", contagem: c?.suspensas }, { valor: "a-cancelar" as const, rotulo: "A cancelar", contagem: c?.a_cancelar }] : []),
              { valor: "a-vencer", rotulo: "A vencer (7 dias)", contagem: c?.a_vencer_7_dias },
              { valor: "pagas", rotulo: "Pagas" },
            ]}
          />
        </div>
        <div className="mtc-linha" style={{ gap: "var(--e-3)", flex: comEtapa ? "1 1 460px" : "0 1 280px", alignItems: "flex-start" }}>
          {comEtapa && (
            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
              <CampoSelecao rotulo="Etapa" valor={etapa} aoMudar={(v) => filtrar({ etapa: v })} opcoes={ETAPAS_DO_FILTRO} />
            </div>
          )}
          {/* A lista mostra as 200 mais antigas: sem procurar, um cliente no fim da fila nao aparece. */}
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <Campo rotulo="Procurar cliente" valor={busca} aoMudar={setBusca} placeholder={comTaxa ? "Nome ou CPF" : "Nome, CPF ou subconta"} dica={procurando ? "Mostrando só quem bate com a busca." : "A partir de 3 letras."} />
          </div>
        </div>
      </div>
      {aba === "a-cancelar" && <Aviso tipo="info">O prazo pra pagar acabou e o plano segue suspenso. Pelo regulamento a Funded é cancelada: abra o plano, encerre por falta de pagamento e cancele a conta na extranet. O sistema não encerra sozinho.</Aviso>}
      {lista.isLoading ? <Carregando compacto texto={comTaxa ? "Buscando as taxas" : "Buscando as mensalidades"} /> : lista.isError ? <Aviso tipo="erro">{erroDe(lista.error)}</Aviso> : <ListaDeMensalidades itens={lista.data?.itens ?? []} aba={aba} filtrada={Boolean(etapa || procurando)} emDolar={comTaxa} aoConcluir={concluir} />}
    </Pilha>
  );
}

/** Dinheiro sem quebrar entre o "R$" e o numero, no numero grande do indicador. */
const reaisInteiros = (c: number) => formatarReais(c).replace(" ", "\u00a0");

/**
 * As contagens (041, 3.6), no topo de Mensalidades: vencidas e quanto, a vencer em 7 dias e
 * o recebido no mes (com quantas foram pagas). Os numeros sao do backend.
 */
function NumerosDasMensalidades({ c, carregando, erro, minimo = 220 }: { c?: ContagensDeMensalidade; carregando: boolean; erro?: string; minimo?: number }) {
  if (erro) return <Aviso tipo="erro">{erro}</Aviso>;
  const pronto = !carregando && c ? c : null;
  // Os numeros da taxa de manutencao (spec 064) so aparecem quando a API manda os campos.
  const daTaxa = c?.suspensas != null;
  const pagas = (n: number) => (daTaxa ? `${n} ${n === 1 ? "taxa paga" : "taxas pagas"}` : `${n} ${n === 1 ? "mensalidade paga" : "mensalidades pagas"}`);
  return (
    <Grade minimo={minimo}>
      <Indicador rotulo="Vencidas" valor={pronto ? String(pronto.vencidas) : "..."} detalhe={pronto ? (pronto.vencidas ? `${formatarReais(pronto.vencidas_centavos)} em atraso` : "Nada em atraso") : undefined} tom={pronto?.vencidas ? "erro" : "neutro"} />
      {daTaxa && pronto && (
        <>
          <Indicador rotulo="Planos suspensos" valor={String(pronto.suspensas ?? 0)} detalhe={pronto.suspensas ? "No prazo pra pagar: bloqueie na extranet" : "Nenhum suspenso por atraso"} tom={pronto.suspensas ? "alerta" : "neutro"} />
          <Indicador rotulo="A cancelar" valor={String(pronto.a_cancelar ?? 0)} detalhe={pronto.a_cancelar ? "Prazo encerrado: confirme o cancelamento" : "Nenhum esperando você"} tom={pronto.a_cancelar ? "erro" : "neutro"} />
        </>
      )}
      <Indicador rotulo="A vencer em 7 dias" valor={pronto ? String(pronto.a_vencer_7_dias) : "..."} detalhe={pronto ? (pronto.a_vencer_7_dias ? "Os lembretes saem sozinhos" : "Nada vencendo") : undefined} />
      <Indicador rotulo="Recebido no mês" valor={pronto ? reaisInteiros(pronto.recebido_no_mes_centavos) : "..."} detalhe={pronto ? pagas(pronto.pagas_no_mes) : undefined} />
      {daTaxa && pronto && (
        <Indicador rotulo="Funded sem taxa" valor={String(pronto.funded_sem_taxa ?? 0)} detalhe={pronto.funded_sem_taxa ? <LinkInterno href="/inscricoes?aba=pendencia">Lance o primeiro vencimento</LinkInterno> : "Toda Funded está com a taxa correndo"} tom={pronto.funded_sem_taxa ? "alerta" : "neutro"} />
      )}
    </Grade>
  );
}

function ListaDeMensalidades({ itens, aba, filtrada, emDolar = false, aoConcluir }: { itens: LinhaDeMensalidade[]; aba: Aba; filtrada: boolean; emDolar?: boolean; aoConcluir: (t: string) => void }) {
  const vazio = emDolar
    ? (filtrada ? "Nenhuma taxa com esta busca." : aba === "vencidas" ? "Nenhuma taxa vencida." : aba === "suspensas" ? "Nenhum plano suspenso por atraso." : aba === "a-cancelar" ? "Nenhum cancelamento esperando você." : aba === "a-vencer" ? "Nada vencendo nos próximos 7 dias." : "Nenhuma taxa paga ainda.")
    : filtrada ? "Nenhuma mensalidade com estes filtros." : aba === "vencidas" ? "Nenhuma mensalidade vencida." : aba === "a-vencer" ? "Nada vencendo nos próximos 7 dias." : "Nenhuma mensalidade paga ainda.";
  return (
    <Tabela
      linhas={itens}
      chave={(p) => p.id}
      vazio={<Vazio titulo="Nada por aqui">{vazio}</Vazio>}
      colunas={[
        { chave: "cliente", rotulo: "Cliente", celula: (p) => <><LinkInterno href={`/matriculas/${p.matricula.id}`}>{p.pessoa.nome}</LinkInterno><span className="mtc-tabela__apoio mtc-mono">{p.pessoa.cpf}</span></> },
        { chave: "plano", rotulo: "Plano", celula: (p) => <>{p.matricula.plano.nome}<span className="mtc-tabela__apoio">{tituloDaParcela(p)}{p.do_avanco ? " (no avanço)" : ""}{p.etapa ? `, ${ETAPA[p.etapa]}` : ""}</span><SubcontaDoPlano subconta={p.matricula.subconta} /></> },
        { chave: "vencimento", rotulo: "Vencimento", celula: (p) => <><span className="mtc-mono">{p.vencimento_formatado}</span>{p.dias_de_atraso > 0 && <span className="mtc-tabela__apoio">{p.dias_de_atraso} {p.dias_de_atraso === 1 ? "dia" : "dias"} de atraso</span>}</> },
        // Taxa de manutencao (spec 064): o valor em reais com o dolar e a cotacao do dia em que a parcela nasceu.
        { chave: "valor", rotulo: "Valor", celula: (p) => <><span className="mtc-mono">{p.valor_centavos != null ? formatarReais(p.valor_centavos) : emDolar ? "Aguardando a cotação" : "A definir"}</span>{emDolar && p.valor_usd_centavos != null && <span className="mtc-tabela__apoio"><span className="mtc-mono">{formatarMoeda(p.valor_usd_centavos, "USD")}</span>{p.cotacao ? <> (dólar a <span className="mtc-mono">{cotacaoEmReais(p.cotacao)}</span>)</> : null}</span>}</> },
        { chave: "situacao", rotulo: "Situação", celula: (p) => <><Selo tom={TOM_DA_MENSALIDADE[p.situacao]}>{p.situacao_rotulo}</Selo>{p.status === "PAGA" && <span className="mtc-tabela__apoio">{p.como_pagou === "POR_FORA" ? "Por fora" : "Pela plataforma"}{p.paga_em ? `, ${p.como_pagou === "POR_FORA" ? formatarData(p.paga_em) : formatarDataHora(p.paga_em)}` : ""}</span>}{p.suspensa_ate && <span className="mtc-tabela__apoio">{p.prazo_encerrado ? `Prazo pra pagar acabou em ${formatarDia(p.suspensa_ate)}` : `Pode pagar até ${formatarDia(p.suspensa_ate)}`}</span>}</> },
        { chave: "acao", rotulo: "Ações", rotuloOculto: true, celula: (p) => <AcoesDaParcela p={p} contexto={`${p.pessoa.nome}, ${p.matricula.plano.nome}`} aoConcluir={aoConcluir} /> },
      ]}
    />
  );
}

// ---------------------------------------------------------------- acoes por parcela (spec 041)

const BOTOES: { acao: AcaoDaParcela; rotulo: string; variante: "secundario" | "fantasma"; papel: "SUPORTE" | "ADMIN" }[] = [
  { acao: "marcar_paga", rotulo: "Marcar paga", variante: "secundario", papel: "SUPORTE" },
  { acao: "mudar_vencimento", rotulo: "Mudar vencimento", variante: "fantasma", papel: "ADMIN" },
  { acao: "cancelar", rotulo: "Cancelar", variante: "fantasma", papel: "ADMIN" },
  { acao: "desfazer_paga", rotulo: "Desfazer paga", variante: "fantasma", papel: "ADMIN" },
];

/**
 * Os botoes de uma parcela, so os que vieram em `acoes`, e o dialogo de cada um. `contexto` (quem e qual plano) entra na
 * descricao do dialogo quando a tela nao deixa isso claro (a lista de Mensalidades).
 */
export function AcoesDaParcela({ p, contexto, aoConcluir }: { p: Mensalidade; contexto?: string; aoConcluir: (t: string) => void }) {
  const { pode } = usePapel();
  const [aberta, setAberta] = useState<AcaoDaParcela | null>(null);
  const botoes = BOTOES.filter((b) => p.acoes?.includes(b.acao));
  if (!botoes.length) return null;
  const fechar = () => setAberta(null);
  return (
    <>
      <span className="mtc-linha" style={{ gap: "var(--e-1) var(--e-2)" }}>
        {botoes.map((b) => (
          <Botao key={b.acao} variante={b.variante} tamanho="pequeno" disabled={!pode(b.papel)} title={pode(b.papel) ? undefined : "Seu papel não permite esta ação."} aria-label={`${b.rotulo}: ${tituloDaParcela(p).toLowerCase()}`} onClick={() => setAberta(b.acao)}>{b.rotulo}</Botao>
        ))}
      </span>
      <MarcarPaga m={aberta === "marcar_paga" ? { ...p, contexto } : null} aoFechar={fechar} aoConcluir={aoConcluir} />
      <MudarVencimento p={p} contexto={contexto} aberto={aberta === "mudar_vencimento"} aoFechar={fechar} aoConcluir={aoConcluir} />
      <CancelarParcela p={p} contexto={contexto} aberto={aberta === "cancelar"} aoFechar={fechar} aoConcluir={aoConcluir} />
      <DesfazerPaga p={p} contexto={contexto} aberto={aberta === "desfazer_paga"} aoFechar={fechar} aoConcluir={aoConcluir} />
    </>
  );
}

const quemE = (contexto?: string) => (contexto ? `${contexto}. ` : "");
const valorDe = (p: Mensalidade) => (p.valor_centavos != null ? formatarReais(p.valor_centavos) : "valor a definir");
const dicaDoMotivo = "Pelo menos 5 letras. Fica na trilha da matrícula; o cliente não recebe e-mail.";

/** Reserva (M8): o comprovante chegou por outro canal. A cobranca aberta e cancelada e a proxima nasce. */
export function MarcarPaga({ m, aoFechar, aoConcluir }: { m: (Mensalidade & { contexto?: string }) | null; aoFechar: () => void; aoConcluir: (t: string) => void }) {
  const { postar } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const [observacao, setObservacao] = useState("");
  return (
    <DialogoDeAcao
      aberto={Boolean(m)}
      aoFechar={aoFechar}
      titulo={m?.titulo ? "Marcar a taxa como paga" : "Marcar a mensalidade como paga"}
      descricao={m ? `${quemE(m.contexto)}${tituloDaParcela(m)} de ${valorDe(m)}, vence em ${m.vencimento_formatado}. Use quando o cliente pagou por fora e mandou o comprovante por outro canal.` : undefined}
      botao="Marcar paga"
      podeEnviar={pode("SUPORTE") && observacao.trim().length >= MOTIVO_MINIMO}
      aoAbrir={() => setObservacao("")}
      aoEnviar={() => postar(`/v1/admin/mensalidades/${m!.id}/marcar-paga`, { observacao: observacao.trim() })}
      aoConcluir={() => { void qc.invalidateQueries(); aoConcluir(`${m ? tituloDaParcela(m) : "Mensalidade"} marcada como paga. O cliente foi avisado e a área desbloqueia na hora.`); }}
    >
      <CampoTextoLongo rotulo="Observação" valor={observacao} aoMudar={setObservacao} maxLength={500} required placeholder="Ex.: comprovante do PIX recebido pelo WhatsApp em 14/09." dica="Fica na mensalidade e na trilha da matrícula." />
    </DialogoDeAcao>
  );
}

/** Mudar o vencimento (041, 3.1): so a proxima em aberto, de hoje em diante; as seguintes andam junto. */
function MudarVencimento({ p, contexto, aberto, aoFechar, aoConcluir }: { p: Mensalidade; contexto?: string; aberto: boolean; aoFechar: () => void; aoConcluir: (t: string) => void }) {
  const { por } = useApi();
  const qc = useQueryClient();
  const [data, setData] = useState("");
  const [motivo, setMotivo] = useState("");
  const titulo = tituloDaParcela(p);
  return (
    <DialogoDeAcao
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={`Mudar o vencimento da ${titulo.toLowerCase()}`}
      descricao={`${quemE(contexto)}${titulo} de ${valorDe(p)}, vence em ${p.vencimento_formatado}. ${p.avulsa ? "Só esta avulsa muda." : p.titulo ? "As seguintes andam junto, no ciclo do plano, a partir da data nova." : "As mensalidades seguintes andam junto, de 30 em 30 dias a partir da data nova."} A cobrança aberta é cancelada e o cliente vê a data nova na área.`}
      botao="Mudar vencimento"
      podeEnviar={Boolean(data) && motivo.trim().length >= MOTIVO_MINIMO}
      aoAbrir={() => { setData(p.vencimento); setMotivo(""); }}
      aoEnviar={async () => {
        const r = await por<{ ok: true; mudadas: number }>(`/v1/admin/mensalidades/${p.id}/vencimento`, { vencimento: data, motivo: motivo.trim() });
        const seguintes = r.mudadas - 1;
        aoConcluir(`${titulo} agora vence em ${formatarDia(data)}${seguintes > 0 ? `, e ${seguintes === 1 ? "a seguinte andou junto" : `as ${seguintes} seguintes andaram junto`}` : ""}.`);
      }}
      aoConcluir={() => void qc.invalidateQueries()}
    >
      <CampoData rotulo="Vencimento novo" valor={data} aoMudar={setData} min={hojeEmSaoPaulo()} dica="De hoje em diante." />
      <CampoTextoLongo rotulo="Motivo" valor={motivo} aoMudar={setMotivo} maxLength={500} required placeholder="Ex.: o cliente recebe no dia 10 e pediu pelo WhatsApp." dica={dicaDoMotivo} />
    </DialogoDeAcao>
  );
}

/** Cancelar a mao (041, 3.4): so em aberto, com o motivo escrito; nao volta sozinha. */
function CancelarParcela({ p, contexto, aberto, aoFechar, aoConcluir }: { p: Mensalidade; contexto?: string; aberto: boolean; aoFechar: () => void; aoConcluir: (t: string) => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState("");
  const titulo = tituloDaParcela(p);
  return (
    <DialogoDeAcao
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={`Cancelar a ${titulo.toLowerCase()}`}
      descricao={`${quemE(contexto)}${titulo} de ${valorDe(p)}, vence em ${p.vencimento_formatado}. Fica cancelada com o motivo, a cobrança aberta é cancelada e ela não volta sozinha.`}
      botao={p.titulo ? "Cancelar taxa" : "Cancelar mensalidade"}
      variante="perigo"
      rotuloFechar="Voltar"
      podeEnviar={motivo.trim().length >= MOTIVO_MINIMO}
      aoAbrir={() => setMotivo("")}
      aoEnviar={() => postar(`/v1/admin/mensalidades/${p.id}/cancelar`, { motivo: motivo.trim() })}
      aoConcluir={() => { void qc.invalidateQueries(); aoConcluir(`${titulo} cancelada.`); }}
    >
      <CampoTextoLongo rotulo="Motivo" valor={motivo} aoMudar={setMotivo} maxLength={500} required placeholder="Ex.: lançada em dobro por engano." dica={dicaDoMotivo} />
    </DialogoDeAcao>
  );
}

/**
 * Desfazer o "paga" (041, 3.2): so a paga por fora. Volta pra a pagar; se ja venceu, a area do cliente bloqueia de novo,
 * e o dialogo avisa antes de confirmar.
 */
function DesfazerPaga({ p, contexto, aberto, aoFechar, aoConcluir }: { p: Mensalidade; contexto?: string; aberto: boolean; aoFechar: () => void; aoConcluir: (t: string) => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState("");
  const titulo = tituloDaParcela(p);
  const jaVenceu = p.vencimento < hojeEmSaoPaulo();
  return (
    <DialogoDeAcao
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={`Desfazer o pagamento da ${titulo.toLowerCase()}`}
      descricao={`${quemE(contexto)}${titulo} de ${valorDe(p)}, vence em ${p.vencimento_formatado}, paga por fora${p.paga_em ? ` em ${formatarData(p.paga_em)}` : ""}. Ela volta para a pagar. Use quando foi marcada como paga por engano.`}
      botao="Desfazer paga"
      variante="perigo"
      podeEnviar={motivo.trim().length >= MOTIVO_MINIMO}
      aoAbrir={() => setMotivo("")}
      aoEnviar={async () => {
        const r = await postar<{ ok: true; vencida: boolean }>(`/v1/admin/mensalidades/${p.id}/desfazer-paga`, { motivo: motivo.trim() });
        aoConcluir(r.vencida ? `Pagamento desfeito. A ${titulo.toLowerCase()} está vencida e a área do cliente voltou a ficar bloqueada até o pagamento.` : `Pagamento desfeito. A ${titulo.toLowerCase()} voltou para a pagar.`);
      }}
      aoConcluir={() => void qc.invalidateQueries()}
    >
      {jaVenceu && <Aviso tipo="alerta">Ela venceu em {p.vencimento_formatado}: ao desfazer, a área do cliente volta a ficar bloqueada até o pagamento.</Aviso>}
      {p.observacao && <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>Observação de quando foi marcada: {p.observacao}</p>}
      <CampoTextoLongo rotulo="Motivo" valor={motivo} aoMudar={setMotivo} maxLength={500} required placeholder="Ex.: marquei a mensalidade errada; o comprovante era de outro cliente." dica={dicaDoMotivo} />
    </DialogoDeAcao>
  );
}

/**
 * Lancar uma mensalidade avulsa (041, 3.3): em plano que paga, com vencimento e valor; sem valor, o backend usa o da
 * plataforma de agora (mostrado na dica). Fora de plano que paga, o backend recusa e a frase dele aparece.
 */
export function LancarMensalidade({ id, valorDeAgora, aberto, aoFechar, aoConcluir }: { id: string; valorDeAgora: number | null; aberto: boolean; aoFechar: () => void; aoConcluir: (t: string) => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const [data, setData] = useState("");
  const [valor, setValor] = useState(0);
  const [motivo, setMotivo] = useState("");
  return (
    <DialogoDeAcao
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Lançar mensalidade"
      descricao="Uma mensalidade avulsa neste plano, com numeração própria (Avulsa 1, Avulsa 2). O cliente paga pela área como as outras e vê a nova parcela lá, sem e-mail."
      botao="Lançar mensalidade"
      podeEnviar={Boolean(data) && motivo.trim().length >= MOTIVO_MINIMO}
      aoAbrir={() => { setData(hojeEmSaoPaulo()); setValor(0); setMotivo(""); }}
      aoEnviar={async () => {
        const r = await postar<{ ok: true; id: string; numero: number }>(`/v1/admin/matriculas/${id}/mensalidades`, { vencimento: data, ...(valor > 0 ? { valor_centavos: valor } : {}), motivo: motivo.trim() });
        aoConcluir(`Avulsa ${r.numero} lançada, vence em ${formatarDia(data)}.`);
      }}
      aoConcluir={() => void qc.invalidateQueries()}
    >
      <CampoData rotulo="Vencimento" valor={data} aoMudar={setData} min={hojeEmSaoPaulo()} dica="De hoje em diante." />
      <CampoDinheiro rotulo="Valor (opcional)" centavos={valor} aoMudar={setValor} dica={valorDeAgora != null ? `Em branco, usa o valor de agora: ${formatarReais(valorDeAgora)}.` : "Em branco, usa o valor da plataforma de agora."} />
      <CampoTextoLongo rotulo="Motivo" valor={motivo} aoMudar={setMotivo} maxLength={500} required placeholder="Ex.: ele deve o mês de outubro, combinado pelo WhatsApp." dica={dicaDoMotivo} />
    </DialogoDeAcao>
  );
}

/**
 * O valor da mensalidade desta matricula (M6): vem do checkout da compra; o administrador informa ou corrige com motivo
 * (checkout criado sem valor, cortesia, engano). As mensalidades em aberto passam pro valor novo.
 */
export function MudarValores({ id, valores, aberto, aoFechar, aoConcluir }: { id: string; valores: NonNullable<ModuloMensalidade["valores"]>; aberto: boolean; aoFechar: () => void; aoConcluir: (t: string) => void }) {
  const { por } = useApi();
  const qc = useQueryClient();
  const [one, setOne] = useState(0);
  const [pro, setPro] = useState(0);
  const [motivo, setMotivo] = useState("");
  return (
    <DialogoDeAcao
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Valor da mensalidade"
      descricao="Vale só para esta matrícula. As mensalidades em aberto passam para o valor novo; as pagas ficam como estão."
      botao="Salvar valores"
      podeEnviar={(one > 0 || pro > 0) && motivo.trim().length >= MOTIVO_MINIMO}
      aoAbrir={() => { setOne(valores.profit_one ?? 0); setPro(valores.profit_pro ?? 0); setMotivo(""); }}
      aoEnviar={() => por(`/v1/admin/matriculas/${id}/mensalidade/valores`, { profit_one: one > 0 ? one : null, profit_pro: pro > 0 ? pro : null, motivo: motivo.trim() })}
      aoConcluir={() => { void qc.invalidateQueries(); aoConcluir("Valores da mensalidade salvos."); }}
    >
      <CampoDinheiro rotulo="Profit One, por mês" centavos={one} aoMudar={setOne} dica="O Profit One paga só no Simulador Remunerado. Zero deixa sem valor." />
      <CampoDinheiro rotulo="Profit Pro, por mês" centavos={pro} aoMudar={setPro} dica="O Profit Pro paga no Simulador e na Mesa Real." />
      <CampoTextoLongo rotulo="Motivo" valor={motivo} aoMudar={setMotivo} maxLength={500} required placeholder="Ex.: o checkout foi criado sem o valor da mensalidade." dica="Fica na trilha da matrícula." />
    </DialogoDeAcao>
  );
}
